import config from "@/config/config";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { approveGameCash } from "@/helper/contractFunctions/MafiaGameBank";
import {
  claimCityProfit,
  crushCars,
  getCityCrusherInfo,
  getNextCrushTime,
  updateCityCrusherSettings,
} from "@/helper/contractFunctions/MafiaCarCrusher";

export interface CrusherInfo {
  bulletProfit: number;
  cashProfit: number;
  inventoryItemId: number;
  bulletFeePerCar: number;
  oneTimeCashFee: number;
  lastUpdateTime: number;
}

export interface CarCrusherState {
  crusherInfo: CrusherInfo | null;
  isLoadingCrusherInfo: boolean;
  isClaimingProfit: boolean;
  isUpdatingSettings: boolean;
  isCrushingCars: boolean;
  isApprovingGameCash: boolean;
  nextCrushTime: number;
}

const initialState: CarCrusherState = {
  crusherInfo: null,
  isLoadingCrusherInfo: false,
  isClaimingProfit: false,
  isUpdatingSettings: false,
  isCrushingCars: false,
  isApprovingGameCash: false,
  nextCrushTime: -1,
};

export const getCrusherInfoAction = createAsyncThunk(
  "carCrusher/getCrusherInfoAction",
  async ({ cityId }: { cityId: number }) => {
    const info = await getCityCrusherInfo(cityId);
    return info;
  }
);

export const getNextCrushTimeAction = createAsyncThunk(
  "carCrusher/getNextCrushTimeAction",
  getNextCrushTime
);

export const claimCrusherProfitAction = createAsyncThunk(
  "carCrusher/claimCrusherProfitAction",
  async ({ account, cityId }: { account: string; cityId: number }) => {
    const txHash = await claimCityProfit(account, cityId);
    return txHash;
  }
);

export const crushCarsAction = createAsyncThunk(
  "carCrusher/crushCarsAction",
  async ({
    account,
    itemIds,
    message,
    signature,
  }: {
    account: string;
    itemIds: number[];
    message: string;
    signature: string;
  }) => {
    const txHash = await crushCars(account, itemIds, message, signature);
    return txHash;
  }
);

export const approveGameCashForCrusherAction = createAsyncThunk(
  "carCrusher/approveGameCashForCrusherAction",
  async ({ account, cashAmount }: { account: string; cashAmount: number }) => {
    const data = await approveGameCash(
      cashAmount,
      config.carCrusherAddress,
      account
    );
    return data.transactionHash || "";
  }
);

export const updateCrusherSettingsAction = createAsyncThunk(
  "carCrusher/updateCrusherSettingsAction",
  async ({
    account,
    cityId,
    bulletFeePerCar,
    oneTimeCashFee,
  }: {
    account: string;
    cityId: number;
    bulletFeePerCar: number;
    oneTimeCashFee: number;
  }) => {
    const txHash = await updateCityCrusherSettings(
      account,
      cityId,
      bulletFeePerCar,
      oneTimeCashFee
    );
    return txHash;
  }
);

export const carCrusherSlice = createSlice({
  name: "carCrusher",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getCrusherInfoAction.pending, (state) => {
        state.isLoadingCrusherInfo = true;
      })
      .addCase(getCrusherInfoAction.fulfilled, (state, { payload }) => {
        state.isLoadingCrusherInfo = false;
        state.crusherInfo = payload;
      })
      .addCase(getCrusherInfoAction.rejected, (state) => {
        state.isLoadingCrusherInfo = false;
      })
      .addCase(getNextCrushTimeAction.fulfilled, (state, { payload }) => {
        state.nextCrushTime = payload;
      })
      .addCase(getNextCrushTimeAction.rejected, (state) => {
        state.nextCrushTime = -1;
      })
      .addCase(claimCrusherProfitAction.pending, (state) => {
        state.isClaimingProfit = true;
      })
      .addCase(claimCrusherProfitAction.fulfilled, (state) => {
        state.isClaimingProfit = false;
      })
      .addCase(claimCrusherProfitAction.rejected, (state) => {
        state.isClaimingProfit = false;
      })
      .addCase(updateCrusherSettingsAction.pending, (state) => {
        state.isUpdatingSettings = true;
      })
      .addCase(updateCrusherSettingsAction.fulfilled, (state) => {
        state.isUpdatingSettings = false;
      })
      .addCase(updateCrusherSettingsAction.rejected, (state) => {
        state.isUpdatingSettings = false;
      })
      .addCase(crushCarsAction.pending, (state) => {
        state.isCrushingCars = true;
      })
      .addCase(crushCarsAction.fulfilled, (state) => {
        state.isCrushingCars = false;
      })
      .addCase(crushCarsAction.rejected, (state) => {
        state.isCrushingCars = false;
      })
      .addCase(approveGameCashForCrusherAction.pending, (state) => {
        state.isApprovingGameCash = true;
      })
      .addCase(approveGameCashForCrusherAction.fulfilled, (state) => {
        state.isApprovingGameCash = false;
      })
      .addCase(approveGameCashForCrusherAction.rejected, (state) => {
        state.isApprovingGameCash = false;
      });
  },
});

export default carCrusherSlice.reducer;
